import axios from "axios";
import {
  Api_GenerateInternalToken,
  Api_GenerateSso_New,
  Api_GenerateSso_Old,
  Api_GenerateOtp,
  Api_ValidateOtp,
  Api_ValidateSso_New,
  Api_ValidateSso_Old,
  Api_GenerateTokenV2,
  Api_KnowYourPolicyFileUpload,
  Api_ValidateMpin,
  Api_GetCustomerLobDetails,
} from "../utils/ApiEndpoints";
import { DEVICE_TYPE, errorResponse } from "../utils/constants";
import {
  E_ChannelOptions,
  T_CampaignHeaders,
} from "../globalTypes/GlobalTypes";
import CsSessionStorageUtils from "../utils/CsSessionStorageUtils";
import { isEmpty, isEqual } from "lodash";
import { getCookie, INTERNAL_AUTH_TOKEN } from "../utils/CSCookieHandler";
import JSEncrypt from "jsencrypt";
import KypEncryptionService from "./KypEncryptionService";
import { setError } from "../redux-store/reducers/CsErrorSlice";
import {
  apiListForAccountTakeOver,
  apiListToAvoidResTokenValidation,
  encryptedRequestAPIList,
  encryptedResponseAPIList,
} from "./axiosApiLists";
import {
  createEncryptedStatement,
  decryptString,
  encryptAccountTakeOverPayload,
  encryptString,
  getCachedEncryptionKeys,
  getLoginSource,
  parseDeep,
  performDecryption,
} from "./axiosEncryptionUtils";

let store: any = undefined;
const publicKey = process.env.REACT_APP_PUBLIC_KEY || "";
const secretKey256 = process.env.REACT_APP_256_SECRET_KEY || "";
const jsEncrypt = new JSEncrypt();
jsEncrypt.setPublicKey(publicKey);
let statement = "";
let sessionKey = "";
let syncToken = "";

export const injectStore = (_store: any) => {
  store = _store;
};

export const getDeviceType = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  const isMobile = /iphone|ipad|ipod|android|windows phone/g.test(userAgent);
  const isPWA = window.matchMedia("(display-mode: standalone)").matches;
  let temp = DEVICE_TYPE.WEB;
  if (isPWA) {
    if (isMobile) {
      temp = DEVICE_TYPE.PWA_MOBILE;
    } else {
      temp = DEVICE_TYPE.PWA_WEB;
    }
  } else if (isMobile) {
    if (isEmpty(store.getState()?.CsAppSlice?.NativeDeviceInfo)) {
      temp = DEVICE_TYPE.ABC_WEB_MOB_CS; //Mobile Web
    } else {
      temp = DEVICE_TYPE.MOBILE; //Mobile Native
    }
  } else {
    temp = DEVICE_TYPE.WEB;
  }
  return temp;
};

const axiosInstance = axios.create({
  headers: {
    "Content-Type": "application/json",
    // Add any other headers or configurations you need
  },
});

// Add a request interceptor
axiosInstance.interceptors.request.use(
  async (config) => {
    // You can modify the request config here`, e.g., add authentication headers

    //The changes below are for OSP-7617. If we want to apply the same changes to other APIs, please add them to the apiListForAccountTakeOver list
    if (apiListForAccountTakeOver.includes(config.url || "")) {
      const { iv, encryptedIv } = createEncryptedStatement(jsEncrypt);
      statement = iv;
      config.headers["statement"] = encryptedIv;
      config.headers["requesttoken"] = encryptAccountTakeOverPayload(
        jsEncrypt,
        config.data,
      );
    }

    // Encryption logic for request payload
    if (encryptedRequestAPIList.includes(config.url || "")) {
      // Get cached encryption keys (or fetch and cache if not available)
      const keys = getCachedEncryptionKeys(store, config.url || "");
      if (keys && config.data && Object.keys(config.data).length) {
        const requestDataString = JSON.stringify(config.data); // Convert request data to string
        const encryptedRequest = encryptString(
          requestDataString,
          keys.iv,
          keys.key,
        ); // Encrypt the request data
        config.data = { request: encryptedRequest }; // Replace the original data with the encrypted one
      }
    }

    if (Api_KnowYourPolicyFileUpload === config.url) {
      const encryptedData = await KypEncryptionService.encrypt(
        JSON.stringify(config.data),
      );
      config.data = { encryptedData: encryptedData };
    }

    let externalToken = undefined;
    let internalToken = undefined;
    let source = undefined;
    let csUserId = undefined;
    let traceId = undefined;
    let loginSource = undefined;
    let browserVersion = undefined;
    let browserName = undefined;
    let channel = undefined;
    let nativeDeviceInfo = undefined;
    let ipAddress = undefined;
    const isPseudoUserLoggedIn =
      store.getState()?.PseudoLoginSlice?.isPseudoLoggedIn;
    const ssoSlice = store.getState()?.SsoSlice;
    const redirectionUrlFromSsoSlice = ssoSlice?.ssoData.redirectionUrl;
    const adminIdForPseudoUser =
      store.getState()?.PseudoLoginSlice.adminIdFromPseudo;

    try {
      const csAppSlice = store.getState()?.CsAppSlice;
      nativeDeviceInfo = csAppSlice?.NativeDeviceInfo;
      ipAddress = csAppSlice?.ipAddress;
      browserName = csAppSlice.userAgent?.browserName;
      browserVersion = csAppSlice.userAgent?.browserVersion;
      externalToken = csAppSlice.tokenDetails?.external?.access_token;
      internalToken = csAppSlice.tokenDetails?.internal?.authToken;
      source = CsSessionStorageUtils.get("DEVICE_TYPE") || getDeviceType();
      traceId = CsSessionStorageUtils.get("TRACE_ID") || csAppSlice.traceId;
      channel = store.getState()?.CsAppSlice.channel;
      loginSource = getLoginSource(csAppSlice.channel);
      csUserId = csAppSlice.csUserId || csAppSlice.userDetails.CS_USER_ID;
    } catch (err) {}
    if (
      [Api_GenerateSso_New, Api_GenerateSso_Old].includes(config?.url || "")
    ) {
      config.headers["cognito-token "] = `Bearer ${externalToken}`;
    } else if (
      [Api_ValidateSso_New, Api_ValidateSso_Old].includes(config?.url || "")
    ) {
      config.headers["cognito-token "] = `Bearer ${externalToken}`;
    } else if (config.url !== "/auth/generate-token") {
      config.headers["authToken"] = externalToken;
      config.headers["Authorization"] = `Bearer ${internalToken}`;
    }
    if (channel === E_ChannelOptions.SECONDARY_CHANNEL) {
      /**
       * to add utm related headers in case of secondary channels
       */
      const campaignIdentifiers: T_CampaignHeaders[] = JSON.parse(
        CsSessionStorageUtils.get("CAMPAIGN_IDENTIFIERS"),
      );
      campaignIdentifiers?.forEach((obj: T_CampaignHeaders) => {
        config.headers[obj.key] = obj.value;
      });
    }
    if (config.url === Api_GenerateInternalToken) {
      const loginSlice = store.getState()?.LoginSlice;
      const pseudoLoginSlice = store.getState()?.PseudoLoginSlice;
      const encryptedChannel = jsEncrypt.encrypt(channel);
      const oldCookieInternalToken = getCookie(INTERNAL_AUTH_TOKEN);
      if (!isEmpty(nativeDeviceInfo)) {
        const mobileModel = nativeDeviceInfo?.deviceName;
        const mobileMakeModel = nativeDeviceInfo?.deviceModel;
        const mobileOs = nativeDeviceInfo?.systemName;
        const mobileOsVersion = nativeDeviceInfo?.systemVersion;
        const deviceId = nativeDeviceInfo?.identifier;
        const appVersion = nativeDeviceInfo?.appVersion;
        config.headers["mobileModel"] = mobileModel;
        config.headers["mobileMakeModel"] = mobileMakeModel;
        config.headers["mobileOs"] = mobileOs;
        config.headers["mobileOsVersion"] = mobileOsVersion;
        config.headers["deviceId"] = deviceId;
        config.headers["appVersion"] = appVersion;
      }
      config.headers["ipAddress"] = ipAddress;
      config.headers["oldToken"] = oldCookieInternalToken;
      config.headers["sessionkey"] = sessionKey;
      config.headers["channel"] =
        loginSlice?.isViaBioMeticLogin || pseudoLoginSlice?.isPseudoLoggedIn
          ? undefined
          : encryptedChannel;
      sessionKey = "";
    }
    config.headers["browser"] = browserName;
    config.headers["browserVersion"] = browserVersion;
    config.headers["csUserId"] = csUserId;
    config.headers["source"] = source;
    config.headers["loginSource"] = loginSource;
    config.headers["traceId"] = traceId;
    config.headers["pageName"] = window.location.pathname;
    if (
      isEmpty(redirectionUrlFromSsoSlice) ||
      [Api_GenerateSso_New, Api_GenerateSso_Old].includes(config?.url || "")
    ) {
      //added for the osp-2635, remove if not needed in future
      config.headers["apiCategory"] = isPseudoUserLoggedIn
        ? "INTERNAL_REF_SUPP"
        : "INTERNAL";
    }
    if (isPseudoUserLoggedIn) {
      //added as part of OSP-6445
      config.headers["adminId"] = adminIdForPseudoUser;
    }
    if (config.url === Api_GenerateTokenV2) {
      const { iv, encryptedIv } = createEncryptedStatement(jsEncrypt);
      statement = iv;
      config.headers["statement"] = encryptedIv;
    }

    if (config.url === Api_GetCustomerLobDetails) {
      config.headers["syncToken"] = syncToken;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Add a response interceptor
axiosInstance.interceptors.response.use(
  async (response) => {
    if (
      [Api_ValidateOtp, Api_ValidateMpin].includes(response.config.url || "")
    ) {
      sessionKey = response?.headers?.["sessionkey"] || "";
    }

    if ([Api_ValidateOtp].includes(response.config.url || "")) {
      syncToken = response?.headers?.["synctoken"] || "";
    }

    if (response.config.url === Api_GenerateTokenV2) {
      let decryptedResponse = decryptString(
        response.data.payload,
        statement,
        secretKey256,
      );
      let transformedResponse = decryptedResponse;
      if (decryptedResponse) {
        transformedResponse = JSON.parse(decryptedResponse);
      }
      response.data.payload = transformedResponse;
      statement = "";
    }

    // You can modify the response data here,
    if (encryptedResponseAPIList.includes(response.config.url || "")) {
      // Get cached encryption keys instead of fetching from Redux again
      const keys = getCachedEncryptionKeys(store, response.config.url || "");

      if (keys) {
        const IV = keys.iv;
        const secretKey = keys.key;
        let decryptWholeResponse = false;
        if (
          [Api_ValidateOtp, Api_GenerateOtp].includes(response.config.url || "")
        ) {
          decryptWholeResponse = true;
        }

        performDecryption(response, IV, secretKey, decryptWholeResponse);
      }
    }

    //The changes below (539–563) are for OSP-7617. If we want to apply the same changes to other APIs, please add them to the apiListForAccountTakeOver list
    if (
      apiListForAccountTakeOver.includes(response.config.url || "") &&
      !apiListToAvoidResTokenValidation.includes(response.config.url || "")
    ) {
      const encryptedResponseTokenFromHeader =
        response?.headers["responsetoken"];
      let decryptedResponseToken = decryptString(
        encryptedResponseTokenFromHeader,
        statement,
        secretKey256,
      );
      const parsedDecryptedResponseToken = parseDeep(decryptedResponseToken); //parsing the object for better usage
      if (
        !isEqual(response.data.payload, parsedDecryptedResponseToken["payload"]) //comparing the payload from original api response & payload fetched from responseToken, if matches then proceeds further
      ) {
        store.dispatch(
          setError({
            error: new Error(),
            message: "Invalid Request",
          }),
        );
        response.data.payload = null;
        response.data = { ...response.data, ...errorResponse };
        return response;
      }
      response.data.headers = response.headers;
      response.data.headers["responsetoken"] = decryptedResponseToken;
    }

    // KYP Service decryption logic for response payload
    if (Api_KnowYourPolicyFileUpload === response.config.url) {
      const decryptedResponse = await KypEncryptionService.decrypt(
        response.data?.encryptedData,
      );
      response.data = decryptedResponse;
    }

    return response;
  },
  (error) => {
    return Promise.reject(error);
  },
);

export default axiosInstance;
